"use client";

import { isFilled } from "@prismicio/client";
import { PrismicImage } from "@prismicio/react";
import "@splidejs/react-splide/css/core";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { useEffect, useState } from "react";

const PicturesDesktop = ({ pictures, state, date_sold, date_rented }) => {
    const [isAllPicturesOpen, setIsAllPicturesOpen] = useState(false);
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(1);
    const [startSlideIndex, setStartSlideIndex] = useState(0);
    const normalizedState =
        typeof state === "string"
            ? state
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
                .trim()
            : "";
    const isSold = normalizedState === "vendu";
    const isRented = normalizedState === "loue";
    const soldDate = typeof date_sold === "string" ? date_sold.trim() : "";
    const rentedDate = typeof date_rented === "string" ? date_rented.trim() : "";
    const statusLabel = isSold
        ? soldDate
            ? `Vendu ${soldDate}`
            : "Vendu"
        : isRented
            ? rentedDate
                ? `Loué ${rentedDate}`
                : "Loué"
            : "";
    const allPictures = Array.isArray(pictures)
        ? pictures.filter((item) => isFilled.image(item?.picture))
        : [];
    const picturesSlots = Array.from({ length: 5 }, (_, index) => allPictures[index] ?? null);

    useEffect(() => {
        if (isAllPicturesOpen) {
            setCurrentSlide(startSlideIndex + 1);
        }
    }, [isAllPicturesOpen, startSlideIndex]);

    useEffect(() => {
        if (!isAllPicturesOpen) {
            setIsOverlayVisible(false);
            return;
        }

        const rafId = requestAnimationFrame(() => {
            setIsOverlayVisible(true);
        });

        return () => cancelAnimationFrame(rafId);
    }, [isAllPicturesOpen]);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;

        if (isAllPicturesOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = previousOverflow;
        }

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isAllPicturesOpen]);

    return (
        <>
            <div className="md:pt-6 w-full md:bg-bleu-50 md:pb-10 md:rounded-b-[40px]">
                <div className="relative framing padding-x grid grid-cols-4 grid-rows-[repeat(2,180px)] lg:grid-rows-[repeat(2,260px)] gap-2">
                    {statusLabel && (
                        <div className="framing padding-x absolute top-4 left-4 z-20 pointer-events-none">
                            <p className="text-tag tag tag-terracota-400">{statusLabel}</p>
                        </div>
                    )}
                    {picturesSlots.map((item, index) => (
                        <div
                            key={`property-picture-slot-${index}`}
                            className={`${index === 0 ? "col-span-2 row-span-2" : ""} overflow-hidden rounded-lg bg-bleu-300`}
                            onClick={() => {
                                if (!item) return;
                                setStartSlideIndex(index);
                                setIsAllPicturesOpen(true);
                            }}
                        >
                            {item && (
                                <PrismicImage
                                    field={{
                                        ...item.picture,
                                        alt: item.picture.alt || `Photo du bien ${index + 1}`,
                                    }}
                                    className="w-full h-full object-cover hover:scale-125 transition-transform duration-300 cursor-pointer"
                                />
                            )}
                        </div>
                    ))}
                    {allPictures.length > 0 && (
                        <button
                            type="button"
                            onClick={() => {
                                setStartSlideIndex(0);
                                setIsAllPicturesOpen(true);
                            }}
                            className="absolute bottom-5 right-10 lg:right-25 2xl:right-15 flex gap-2.5 cta cta-bleu-50 items-center body-m justify-center"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_114_920)">
                                    <path d="M19 0L13 0C11.6744 0.00158786 10.4036 0.528882 9.46622 1.46622C8.52888 2.40356 8.00159 3.67441 8 5V5.1C6.87141 5.3321 5.85735 5.94618 5.12872 6.83875C4.40009 7.73132 4.00145 8.8478 4 10V10.1C2.87141 10.3321 1.85735 10.9462 1.12872 11.8387C0.40009 12.7313 0.00145452 13.8478 0 15L0 19C0.00158786 20.3256 0.528882 21.5964 1.46622 22.5338C2.40356 23.4711 3.67441 23.9984 5 24H11C12.3256 23.9984 13.5964 23.4711 14.5338 22.5338C15.4711 21.5964 15.9984 20.3256 16 19V18.9C17.1286 18.6679 18.1427 18.0538 18.8713 17.1613C19.5999 16.2687 19.9985 15.1522 20 14V13.9C21.1286 13.6679 22.1427 13.0538 22.8713 12.1613C23.5999 11.2687 23.9985 10.1522 24 9V5C23.9984 3.67441 23.4711 2.40356 22.5338 1.46622C21.5964 0.528882 20.3256 0.00158786 19 0V0ZM2 15C2 14.2044 2.31607 13.4413 2.87868 12.8787C3.44129 12.3161 4.20435 12 5 12L11 12C11.5437 12.0015 12.0766 12.1512 12.5415 12.4332C13.0063 12.7151 13.3855 13.1185 13.638 13.6L10.183 17.063L9.708 16.584C9.3539 16.2272 8.87802 16.0176 8.37579 15.997C7.87356 15.9764 7.38211 16.1464 7 16.473L2.379 20.433C2.13318 19.9952 2.00275 19.5021 2 19L2 15ZM14 19C14 19.7956 13.6839 20.5587 13.1213 21.1213C12.5587 21.6839 11.7956 22 11 22H5C4.61656 21.9992 4.23691 21.9241 3.882 21.779L8.288 18L8.764 18.481C9.13906 18.8559 9.64767 19.0666 10.178 19.0666C10.7083 19.0666 11.2169 18.8559 11.592 18.481L14 16.068V19ZM18 14C17.9974 14.6183 17.8039 15.2206 17.4459 15.7247C17.0879 16.2288 16.5829 16.6099 16 16.816V15C15.9984 13.6744 15.4711 12.4036 14.5338 11.4662C13.5964 10.5289 12.3256 10.0016 11 10L6 10C6 9.20435 6.31607 8.44129 6.87868 7.87868C7.44129 7.31607 8.20435 7 9 7L15 7C15.7956 7 16.5587 7.31607 17.1213 7.87868C17.6839 8.44129 18 9.20435 18 10V14ZM22 9C21.9974 9.61826 21.8039 10.2206 21.4459 10.7247C21.0879 11.2288 20.5829 11.6099 20 11.816V10C19.9984 8.67441 19.4711 7.40356 18.5338 6.46622C17.5964 5.52888 16.3256 5.00159 15 5L10 5C10 4.20435 10.3161 3.44129 10.8787 2.87868C11.4413 2.31607 12.2044 2 13 2L19 2C19.7956 2 20.5587 2.31607 21.1213 2.87868C21.6839 3.44129 22 4.20435 22 5V9ZM4 15C4 14.8022 4.05865 14.6089 4.16853 14.4444C4.27841 14.28 4.43459 14.1518 4.61732 14.0761C4.80004 14.0004 5.00111 13.9806 5.19509 14.0192C5.38907 14.0578 5.56725 14.153 5.70711 14.2929C5.84696 14.4327 5.9422 14.6109 5.98079 14.8049C6.01937 14.9989 5.99957 15.2 5.92388 15.3827C5.84819 15.5654 5.72002 15.7216 5.55557 15.8315C5.39112 15.9414 5.19778 16 5 16C4.73478 16 4.48043 15.8946 4.29289 15.7071C4.10536 15.5196 4 15.2652 4 15Z" fill="#144154" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_114_920">
                                        <rect width="24" height="24" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                            Voir tous
                        </button>
                    )}
                </div>
            </div>

            {allPictures.length > 0 && isAllPicturesOpen && (
                <div className={`w-screen h-screen bg-bleu-50 py-20 fixed z-100 top-0 property transition-opacity duration-300 ${isOverlayVisible ? "opacity-100" : "opacity-0"}`}>
                    <div className="absolute top-10 left-10 z-110 flex gap-2.5">
                        {statusLabel && (
                            <p className="text-tag tag tag-terracota-400">{statusLabel}</p>
                        )}
                    </div>
                    <div className="absolute top-10 right-10 z-110 flex gap-2.5">
                        <span className="body-xs font-semibold text-bleu-950 self-center">
                            {currentSlide} sur {allPictures.length}
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsAllPicturesOpen(false)}
                            className="cursor-pointer hover:scale-110 transition-transform duration-300"
                            aria-label="Fermer"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#close-icon-clip)">
                                    <path d="M15.9999 15.9999C16.1874 15.8124 16.2927 15.5581 16.2927 15.2929C16.2927 15.0278 16.1874 14.7735 15.9999 14.5859L13.4139 11.9999L15.9999 9.41395C16.1821 9.22535 16.2829 8.97274 16.2806 8.71055C16.2783 8.44835 16.1732 8.19754 15.9878 8.01213C15.8023 7.82672 15.5515 7.72156 15.2893 7.71928C15.0271 7.717 14.7745 7.81779 14.5859 7.99995L11.9999 10.5859L9.41394 7.99995C9.22534 7.81779 8.97274 7.717 8.71054 7.71928C8.44834 7.72156 8.19753 7.82672 8.01212 8.01213C7.82671 8.19754 7.72154 8.44835 7.71927 8.71055C7.71699 8.97274 7.81778 9.22535 7.99994 9.41395L10.5859 11.9999L7.99994 14.5859C7.81778 14.7745 7.71699 15.0271 7.71927 15.2893C7.72154 15.5515 7.82671 15.8023 8.01212 15.9878C8.19753 16.1732 8.44834 16.2783 8.71054 16.2806C8.97274 16.2829 9.22534 16.1821 9.41394 15.9999L11.9999 13.4139L14.5859 15.9999C14.7735 16.1874 15.0278 16.2927 15.2929 16.2927C15.5581 16.2927 15.8124 16.1874 15.9999 15.9999Z" fill="#0C2631" />
                                    <path d="M24 12C24 9.62663 23.2962 7.30655 21.9776 5.33316C20.6591 3.35977 18.7849 1.8217 16.5922 0.913452C14.3995 0.00520006 11.9867 -0.23244 9.65892 0.230582C7.33114 0.693605 5.19295 1.83649 3.51472 3.51472C1.83649 5.19295 0.693599 7.33115 0.230576 9.65892C-0.232448 11.9867 0.00519222 14.3995 0.913445 16.5922C1.8217 18.7849 3.35976 20.6591 5.33315 21.9776C7.30655 23.2962 9.62662 24 12 24C15.1815 23.9966 18.2318 22.7312 20.4815 20.4815C22.7312 18.2318 23.9966 15.1815 24 12H24ZM2 12C2 10.0222 2.58649 8.08879 3.6853 6.4443C4.78412 4.79981 6.3459 3.51809 8.17316 2.76121C10.0004 2.00433 12.0111 1.8063 13.9509 2.19215C15.8907 2.57801 17.6725 3.53041 19.0711 4.92894C20.4696 6.32746 21.422 8.10929 21.8079 10.0491C22.1937 11.9889 21.9957 13.9996 21.2388 15.8268C20.4819 17.6541 19.2002 19.2159 17.5557 20.3147C15.9112 21.4135 13.9778 22 12 22C9.34873 21.9971 6.80688 20.9426 4.93215 19.0679C3.05741 17.1931 2.00291 14.6513 2 12Z" fill="#0C2631" />
                                </g>
                                <defs>
                                    <clipPath id="close-icon-clip">
                                        <rect width="24" height="24" fill="white" transform="translate(24 1.04907e-06) rotate(90)" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    </div>
                    <Splide
                        key={`property-splide-${startSlideIndex}`}
                        options={{
                            type: "loop",
                            perPage: 1,
                            perMove: 1,
                            start: startSlideIndex,
                            gap: "0rem",
                            pagination: true,
                            arrows: true,
                            drag: false,
                        }}
                        onMounted={(splide) => setCurrentSlide(splide.index + 1)}
                        onMoved={(splide) => setCurrentSlide(splide.index + 1)}
                        aria-label="Carrousel plein écran du bien"
                    >
                        {allPictures.map((item, index) => (
                            <SplideSlide key={`property-fullscreen-picture-${index}`} className="flex flex-col items-center justify-center gap-2">
                                <div className="w-full h-full overflow-hidden flex px-20 items-center">
                                    <PrismicImage
                                        field={{
                                            ...item.picture,
                                            alt: item.picture.alt || `Photo du bien ${index + 1}`,
                                        }}
                                        className="max-w-full max-h-full w-auto h-fit object-contain object-center rounded-lg mx-auto bg-bleu-300"
                                    />
                                </div>
                                <span className="body-xs text-bleu-950 min-h-6">
                                    {item.picture.alt}
                                </span>
                            </SplideSlide>
                        ))}
                    </Splide>
                </div>
            )}
        </>
    );
};

export default PicturesDesktop;